// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Text that is in a saved web page and not on the screen.
 *
 * This is a different hiding mechanism from everything else in charsight: the characters are
 * perfectly ordinary, and CSS is what makes them invisible. It is the mechanism behind a saved
 * page or a rendered README that carries instructions for whatever reads it next.
 *
 * TWO HARD RULES, both of which exist because the input is hostile by assumption.
 *
 * 1. **The user's CSS never touches the live document.** The tempting way to resolve a `<style>`
 *    block is to inject it and read computed styles. That would let a stylesheet inside an
 *    untrusted saved page restyle charsight's own interface — including re-triggering the
 *    `[hidden]` display-override trap this fleet has shipped broken twice — and it would hand that
 *    CSS a network capability through `@import` and `url()`. So the stylesheet is parsed as TEXT,
 *    by the small parser below, and `@import` and `url()` are stripped before anything else runs.
 *
 * 2. **It says what it cannot see.** Rules from an external stylesheet, from JavaScript, or inside
 *    a shadow root are invisible to this pass, and so is anything a real layout engine would work
 *    out (an element pushed off-screen by its parent, text the same colour as a background set
 *    three levels up). The panel says that in one sentence rather than implying completeness.
 */

export type HideMechanism =
  | 'display-none'
  | 'visibility-hidden'
  | 'opacity-zero'
  | 'font-size-zero'
  | 'zero-size'
  | 'off-screen'
  | 'clipped'
  | 'hidden-attribute'
  | 'aria-hidden'
  | 'comment';

export interface HtmlFinding {
  mechanism: HideMechanism;
  /** `div.footer` or `<!-- comment -->`. */
  where: string;
  /** The declaration or attribute that does the hiding. */
  how: string;
  /** The text that is there and cannot be seen. Never rendered raw — run it through visibleText. */
  text: string;
  /** True when this is routine authoring rather than a hiding technique. */
  routine: boolean;
}

interface Rule {
  selector: string;
  declarations: Map<string, string>;
}

const MECHANISM_LABELS: Record<HideMechanism, string> = {
  'display-none': 'removed from the layout entirely',
  'visibility-hidden': 'laid out but not painted',
  'opacity-zero': 'painted at zero opacity',
  'font-size-zero': 'set to zero font size',
  'zero-size': 'given no width or height, with the overflow clipped',
  'off-screen': 'positioned outside the visible area',
  clipped: 'clipped away',
  'hidden-attribute': 'marked hidden',
  'aria-hidden': 'hidden from assistive technology',
  comment: 'inside an HTML comment',
};

export function mechanismLabel(m: HideMechanism): string {
  return MECHANISM_LABELS[m];
}

/**
 * A deliberately small CSS parser.
 *
 * It handles what a hiding rule looks like — `selector, selector { prop: value; }` — and nothing
 * else. `@media`, `@supports` and the rest are skipped as a block rather than descended into,
 * because a rule that only applies at a particular width is not a claim charsight can make. That
 * is a known gap and it is stated in the UI, which is the honest way to be incomplete.
 */
export function parseStylesheet(css: string): Rule[] {
  // @import and url() go first, before anything can interpret them. Nothing here ever fetches, but
  // stripping them is the difference between "we do not" and "we cannot".
  const cleaned = css
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/@import[^;]*;/gi, '')
    .replace(/url\([^)]*\)/gi, 'none');

  const rules: Rule[] = [];
  let i = 0;
  while (i < cleaned.length) {
    const open = cleaned.indexOf('{', i);
    if (open < 0) break;
    const prelude = cleaned.slice(i, open).trim();
    if (prelude.startsWith('@')) {
      // Skip the whole at-rule block, matching braces.
      let depth = 0;
      let j = open;
      for (; j < cleaned.length; j++) {
        if (cleaned[j] === '{') depth++;
        else if (cleaned[j] === '}' && --depth === 0) break;
      }
      i = j + 1;
      continue;
    }
    const close = cleaned.indexOf('}', open);
    if (close < 0) break;
    const body = cleaned.slice(open + 1, close);
    const declarations = new Map<string, string>();
    for (const decl of body.split(';')) {
      const colon = decl.indexOf(':');
      if (colon < 0) continue;
      declarations.set(
        decl.slice(0, colon).trim().toLowerCase(),
        decl.slice(colon + 1).trim().toLowerCase(),
      );
    }
    if (prelude && declarations.size) {
      for (const sel of prelude.split(',')) {
        const s = sel.trim();
        if (s) rules.push({ selector: s, declarations });
      }
    }
    i = close + 1;
  }
  return rules;
}

/** Does this declaration block hide its element, and how? */
export function hidingMechanism(decls: Map<string, string>): {
  mechanism: HideMechanism;
  how: string;
} | null {
  const get = (k: string): string => decls.get(k) ?? '';

  if (get('display') === 'none') return { mechanism: 'display-none', how: 'display: none' };
  if (get('visibility') === 'hidden') {
    return { mechanism: 'visibility-hidden', how: 'visibility: hidden' };
  }
  const opacity = get('opacity');
  if (opacity === '0' || opacity === '0.0') {
    return { mechanism: 'opacity-zero', how: `opacity: ${opacity}` };
  }
  const fs = get('font-size');
  if (/^0(px|em|rem|%|pt)?$/.test(fs)) return { mechanism: 'font-size-zero', how: `font-size: ${fs}` };

  const zero = (v: string): boolean => /^0(px|em|rem|%|pt)?$/.test(v);
  if ((zero(get('height')) || zero(get('width'))) && get('overflow') === 'hidden') {
    return { mechanism: 'zero-size', how: 'zero size with overflow: hidden' };
  }
  for (const prop of ['left', 'top', 'right', 'bottom', 'text-indent', 'margin-left']) {
    const v = get(prop);
    const m = /^(-\d+)(px|em|rem|pt)?$/.exec(v);
    if (m && Number(m[1]) <= -1000) {
      return { mechanism: 'off-screen', how: `${prop}: ${v}` };
    }
  }
  const clip = get('clip-path') || get('clip');
  if (clip && (clip.includes('inset(100%') || clip.includes('rect(0'))) {
    return { mechanism: 'clipped', how: `${decls.has('clip-path') ? 'clip-path' : 'clip'}: ${clip}` };
  }
  return null;
}

function describeElement(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const id = el.getAttribute('id');
  const cls = (el.getAttribute('class') ?? '').trim().split(/\s+/).filter(Boolean)[0];
  return `${tag}${id ? `#${id}` : ''}${!id && cls ? `.${cls}` : ''}`;
}

const NEVER_VISIBLE = new Set(['SCRIPT', 'STYLE', 'HEAD', 'TITLE', 'META', 'LINK', 'TEMPLATE']);

function meaningfulText(el: Element): string {
  if (NEVER_VISIBLE.has(el.tagName)) return '';
  const t = (el.textContent ?? '').replace(/\s+/g, ' ').trim();
  return t;
}

/**
 * Scan a parsed HTML document.
 *
 * The caller supplies the `Document`, because `DOMParser` exists only on the main thread — there is
 * no DOMParser in a Web Worker — and because that keeps this function pure and testable.
 */
export function scanHtmlDocument(doc: Document): HtmlFinding[] {
  const out: HtmlFinding[] = [];

  // Same-document stylesheets, parsed as text. `styleEl.sheet` is not reliably populated on a
  // document with no browsing context, and reading it would be the first step towards a CSSOM.
  const rules: Rule[] = [];
  for (const styleEl of Array.from(doc.querySelectorAll('style'))) {
    rules.push(...parseStylesheet(styleEl.textContent ?? ''));
  }
  const hidingRules = rules
    .map((r) => ({ r, hit: hidingMechanism(r.declarations) }))
    .filter((x): x is { r: Rule; hit: { mechanism: HideMechanism; how: string } } => x.hit !== null);

  const seen = new Set<Element>();

  for (const el of Array.from(doc.querySelectorAll('*'))) {
    const text = meaningfulText(el);
    if (!text) continue;

    // Inline style attribute.
    const styleAttr = el.getAttribute('style');
    if (styleAttr) {
      const decls = new Map<string, string>();
      for (const decl of styleAttr.split(';')) {
        const colon = decl.indexOf(':');
        if (colon > 0) {
          decls.set(decl.slice(0, colon).trim().toLowerCase(), decl.slice(colon + 1).trim().toLowerCase());
        }
      }
      const hit = hidingMechanism(decls);
      if (hit && !seen.has(el)) {
        seen.add(el);
        out.push({
          mechanism: hit.mechanism,
          where: describeElement(el),
          how: `style="${hit.how}"`,
          text,
          routine: false,
        });
        continue;
      }
    }

    if (el.hasAttribute('hidden') && !seen.has(el)) {
      seen.add(el);
      out.push({
        mechanism: 'hidden-attribute',
        where: describeElement(el),
        how: 'hidden attribute',
        text,
        routine: false,
      });
      continue;
    }

    for (const { r, hit } of hidingRules) {
      let matches = false;
      try {
        matches = el.matches(r.selector);
      } catch {
        // An invalid or unsupported selector is skipped rather than aborting the scan.
        continue;
      }
      if (!matches || seen.has(el)) continue;
      seen.add(el);
      out.push({
        mechanism: hit.mechanism,
        where: describeElement(el),
        how: `${r.selector} { ${hit.how} }`,
        text,
        routine: false,
      });
      break;
    }

    // aria-hidden on its own is routine — it is how decorative content is kept out of a screen
    // reader — so it is reported as informational and never as a finding of its own weight.
    if (el.getAttribute('aria-hidden') === 'true' && !seen.has(el) && text.length > 24) {
      seen.add(el);
      out.push({
        mechanism: 'aria-hidden',
        where: describeElement(el),
        how: 'aria-hidden="true"',
        text,
        routine: true,
      });
    }
  }

  // Comments. Routine by default — every page is full of them — but they are text that ships with
  // the file and that a model reading the raw HTML will happily follow.
  const walker = doc.createTreeWalker(doc, 128 /* SHOW_COMMENT */);
  let node = walker.nextNode();
  while (node) {
    const text = (node.nodeValue ?? '').replace(/\s+/g, ' ').trim();
    if (text.length > 12) {
      out.push({
        mechanism: 'comment',
        where: '<!-- … -->',
        how: 'HTML comment',
        text,
        routine: true,
      });
    }
    node = walker.nextNode();
  }

  return out;
}
